/** biome-ignore-all lint/suspicious/noExplicitAny: none */
import { type RefObject, useEffect, useLayoutEffect, useRef } from "react";
import { off, on } from "../utils/events";

type Callback = (event: MouseEvent | TouchEvent) => void;

export const useClickAway = <T>(
	cb: Callback,
	initialValue?: T,
): RefObject<T | null> => {
	const ref = useRef<T | null>(initialValue || null);
	const refCb = useRef<Callback>(cb);

	useLayoutEffect(() => {
		refCb.current = cb;
	});

	useEffect(() => {
		const handler = (e: MouseEvent | TouchEvent) => {
			const element: any = ref.current;
			if (element && !element?.contains?.(e.target as Node)) {
				refCb.current(e);
			}
		};

		const escHandler = (e: KeyboardEvent) => {
			if (e.key === "Escape" || e.key === "Esc") {
				// Create a synthetic MouseEvent for compatibility
				const syntheticEvent = new MouseEvent("mousedown", { bubbles: true });
				refCb.current(syntheticEvent);
			}
		};

		on(document, "mousedown", handler);
		on(document, "touchstart", handler);
		on(document, "keydown", escHandler);
		return () => {
			off(document, "mousedown", handler);
			off(document, "touchstart", handler);
			off(document, "keydown", escHandler);
		};
	}, []);

	return ref;
};
